import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Search, ShieldCheck, ShieldOff, Users } from "lucide-react";
import { bloquearDistribuidor, desbloquearDistribuidor, listarDistribuidores } from "../services/api";
import { useFeedbackAlert } from "./common/useFeedbackAlert";

const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";

const ListaDistribuidores = ({ token }) => {
  const { showError, FeedbackAlert } = useFeedbackAlert();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);

  const carregar = async () => {
    const r = await listarDistribuidores(token);
    setItems(Array.isArray(r.data) ? r.data : []);
  };

  useEffect(() => {
    carregar()
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [token]);

  const filtrados = useMemo(() => {
    const termo = busca.toLowerCase();
    if (!termo) return items;
    return items.filter((d) =>
      [d.cnpj, d.razaoSocial, d.nomeFantasia].some((v) => String(v || "").toLowerCase().includes(termo))
    );
  }, [items, busca]);

  const alternar = async (d) => {
    const ativo = toBool(d.ativo);
    if (!window.confirm(`${ativo ? "Bloquear" : "Desbloquear"} o distribuidor "${d.nomeFantasia || d.razaoSocial}"?`)) return;
    try {
      if (ativo) await bloquearDistribuidor(d.id, token);
      else await desbloquearDistribuidor(d.id, token);
      await carregar();
    } catch (err) {
      showError(err, "Erro ao alterar o distribuidor.");
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Carregando...</div>;

  return (
    <>
      <FeedbackAlert />
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Listagem de Distribuidores</h2>
          <p className="text-xs text-gray-500 dark:text-slate-300/80 mt-1">
            Ações: bloquear/desbloquear e usuários de acesso.
          </p>
        </div>
        <div className="rounded-2xl p-4 mb-6 zen-card border-gray-200 dark:bg-white/5 dark:border-white/10">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Buscar</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por CNPJ / Razão / Nome Fantasia"
              className="pl-10 pr-4 py-2.5 w-full rounded-xl border zen-card dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="text-left py-3 px-4">CNPJ</th>
                <th className="text-left py-3 px-4">Razão Social</th>
                <th className="text-left py-3 px-4">Nome Fantasia</th>
                <th className="text-center py-3 px-4">Status</th>
                <th className="text-center py-3 px-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((d) => {
                const ativo = toBool(d.ativo);
                return (
                  <tr key={d.id} className="border-t border-gray-200 dark:border-white/10">
                    <td className="py-3 px-4">{d.cnpj}</td>
                    <td className="py-3 px-4">{d.razaoSocial}</td>
                    <td className="py-3 px-4">{d.nomeFantasia}</td>
                    <td className="text-center py-3 px-4">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${ativo ? "text-green-700 bg-green-50 border-green-200" : "text-red-700 bg-red-50 border-red-200"}`}>
                        {ativo ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                        {ativo ? "Ativo" : "Bloqueado"}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="inline-flex flex-wrap justify-center gap-1">
                        <button type="button" className="px-3 py-2 rounded-xl text-sm border" onClick={() => alternar(d)}>
                          {ativo ? "Bloquear" : "Desbloquear"}
                        </button>
                        <button type="button" className="px-3 py-2 rounded-xl text-sm border inline-flex items-center gap-2" onClick={() => navigate(`/distribuidores/usuarios?distribuidorId=${d.id}`)}>
                          <Users size={16} /> Usuários
                        </button>
                        <button type="button" className="p-2 text-blue-600" onClick={() => navigate(`/distribuidores/editar/${d.id}`)} title="Editar">
                          <Edit size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtrados.length === 0 && (
                <tr><td colSpan={5} className="py-10 text-center text-gray-500">Nenhum distribuidor encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ListaDistribuidores;
